import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import { keys, sumBy, uniq } from 'lodash';
import { IFinancialEntryImportPreviewRowAM, IImportPreviewRowAM, ImportService } from './import-api';
import { ICustomer } from '../customers/customer-service';
import { IScopeAM, ScopeService, IScopePlainAM } from '../scopes/scope-service';
import { DataCopyService } from './data-copy-service';
import { AccountListService, AccountClass } from '../accounts/account-api';
import { ResourcedNotificationService, NotificationType } from '../../components/notifications/notification-service';

@autoinject
export class ImportView {
	@bindable customer: ICustomer;
	@bindable scope: IScopePlainAM;

	importType: string = 'FinancialEntries';
	rows: IImportPreviewRowAM[];
	dimensionValueHeaders: string[];
	sourceCustomers: ICustomer[];
	selectedSourceCustomerId: string;
	sourceScopes: IScopeAM[];
	selectedSourceScopeId: string;
	loadingDataCopy = false;
	invertSignsForFinancialEntries: boolean = false;

	constructor(
		private dataCopyService: DataCopyService,
		private scopeService: ScopeService,
		private importService: ImportService,
		private accountService: AccountListService,
		private notificationService: ResourcedNotificationService) {
	}

	@computedFrom('importType')
	get isFinancialEntryImport(): boolean {
		return this.importType === ImportType.FinancialEntries;
	}

	changeImportType(type: string) {
		this.importType = type;
		this.rows = null;

		if (type === ImportType.FinancialEntries) {
			if (this.sourceCustomers && (!this.sourceCustomers.length || this.sourceCustomers[0].id !== this.customer.id)) {
				this.sourceCustomers.splice(0, 0, this.customer);
			}
		}
		else if (type === ImportType.MeasureEntries) {
			if (this.sourceCustomers && this.sourceCustomers.length && this.sourceCustomers[0].id === this.customer.id) {
				this.sourceCustomers.splice(0, 1);
			}
		}

		this.setSelectedSourceCustomerId(this.sourceCustomers.length == 1 ? this.sourceCustomers[0].id : undefined);
	}

	async handleRows(rows: IImportPreviewRowAM[]) {
		this.rows = rows;

		if (rows.length == 0) { return; }

		this.dimensionValueHeaders = keys((rows[0]).dimensionValues);

		if (this.importType === 'FinancialEntries') {
			this.invertSignsForFinancialEntries = await this.getTotalAmountFromRows(rows) > 0;
		}
	}

	async getTotalAmountFromRows(rows: IImportPreviewRowAM[]): Promise<number> {

		let financialEntryRows = rows.map(r => r as IFinancialEntryImportPreviewRowAM);
		let expenseAccounts = await this.accountService.filterAccountNumbersByAccountClass(this.scope.id, uniq(financialEntryRows.map(f => f.accountNumber)), AccountClass.Expenses);
		let revenueEntries = financialEntryRows.filter(f => expenseAccounts.indexOf(f.accountNumber) >= 0);
		var result = sumBy(revenueEntries, 'amount');

		return result;
	}

	cancel() {
		this.rows = null;
	}

	async customerChanged(newCustomer: ICustomer) {
		this.sourceCustomers = null;
		this.sourceScopes = null;
		this.selectedSourceCustomerId = null;

		let sourceCustomers = await this.dataCopyService.getSourceCustomersForTargetCustomer(newCustomer.id);
		if (this.importType === ImportType.FinancialEntries) {
			sourceCustomers.splice(0, 0, newCustomer);
		}
		this.sourceCustomers = sourceCustomers;

		this.setSelectedSourceCustomerId(sourceCustomers.length == 1 ? sourceCustomers[0].id : undefined);
	}

	// Recalculate potential source scopes when the global scope selection changes
	async scopeChanged() {

		this.setSelectedSourceCustomerId(undefined);
		if (this.sourceCustomers && this.sourceCustomers.length == 1) {
			this.setSelectedSourceCustomerId(this.customer.id);
		}
	}

	async setSelectedSourceCustomerId(id: string) {
		this.sourceScopes = null;
		this.selectedSourceScopeId = null;

		if (id && this.scope) {
			var scopes = await this.scopeService.getScopesWithSameDateRangeForCustomer(id, this.scope.id);
			this.sourceScopes = scopes.filter(s => s.id !== this.scope.id);
		}

		this.selectedSourceCustomerId = id;
	}

	async setSelectedSourceScopeId(id: string) {
		this.selectedSourceScopeId = id;
	}

	async copyDataToImport(financialEntrySourceFilter: string, keepServiceTargeting: string) {
		const handleResponse = async (response: Response) => {
			if (!response.ok) {
				const message = await response.json();
				this.notificationService.pushWithLiteralContent("Label_ErrorInSubmit", NotificationType.Error, message);
				console.log("Unhandled error in request", message);
				this.loadingDataCopy = false;
				return;
			}
			const sentImport = await response.json();
			location.href = `/imports/entry/${sentImport.id}`;
		};

		this.loadingDataCopy = true;

		let response: Response;

		switch (this.importType) {
			case ImportType.FinancialEntries:
				response = await this.importService.copyDataToFinancialEntryImport(this.selectedSourceScopeId, this.scope.id, financialEntrySourceFilter, keepServiceTargeting);
				break;
			case ImportType.MeasureEntries:
				response = await this.importService.copyDataToMeasureEntryImport(this.selectedSourceScopeId, this.scope.id);
				break;
			default:
				throw new Error(`Value ${this.importType} is not a valid import type.`);
		}

		handleResponse(response);
	}
}

export enum ImportType {
	FinancialEntries = 'FinancialEntries',
	MeasureEntries = 'MeasureEntries'
}
